import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, RefreshCw, DollarSign, Calendar, User, Filter, Edit, Trash2, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useTranslation } from 'react-i18next';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import InvoiceGenerator from './InvoiceGenerator';
import TaxReportGenerator from './TaxReportGenerator';
import StudentStatusToggle from './StudentStatusToggle';
import EditPaymentDialog from './payments/EditPaymentDialog';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  payment_status?: string;
}

interface StudentPayment {
  id: string;
  studentName: string;
  email: string;
  category: string;
  subscriptionType: '1year' | '6months' | '3months' | 'monthly';
  paymentPlan: '1' | '2' | '3';
  totalAmount: number;
  amountPaid: number;
  installmentsPaid: number;
  totalInstallments: number;
  status: 'pending' | 'partial' | 'complete';
  nextDueDate?: string;
  startDate?: string;
  endDate?: string;
}

interface StudentPaymentsTabProps {
  studentPayments: any[];
  students: any[];
  onRefresh?: () => void;
  onEditPayment?: (payment: any) => void;
  onDeletePayment?: (paymentId: string) => void;
}

const StudentPaymentsTab: React.FC<StudentPaymentsTabProps> = ({
  studentPayments,
  students,
  onRefresh,
  onEditPayment,
  onDeletePayment
}) => {
  useUiLanguage();
  const { t } = useTranslation(['common', 'payments']);
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [paymentCategories, setPaymentCategories] = useState<any[]>([]);
  const [studentStatuses, setStudentStatuses] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setStudentStatuses(Object.fromEntries(students.filter(student => typeof student.is_active === 'boolean').map(student => [student.id, student.is_active])));
  }, [students]);

  // Get currency from payment categories or default to USD
  const defaultCurrency = paymentCategories.length > 0 && paymentCategories[0].currency
    ? paymentCategories[0].currency
    : "USD";
  const currencySymbol = getCurrencySymbol(defaultCurrency);

  // Fetch payment categories to get currency
  useEffect(() => {
    const fetchPaymentCategories = async () => {
      try {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('dojo_id')
          .eq('id', (await supabase.auth.getUser()).data.user?.id)
          .single();

        if (userProfile?.dojo_id) {
          const { data: categories } = await supabase
            .from('payment_categories')
            .select('currency')
            .eq('dojo_id', userProfile.dojo_id)
            .eq('is_active', true)
            .order('created_at', { ascending: false });

          if (categories && categories.length > 0) {
            setPaymentCategories(categories);
          }
        }
      } catch (error) {
        console.error('Error fetching payment categories:', error);
      }
    };

    fetchPaymentCategories();
  }, []);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'destructive' as const, label: uiText("Pending") },
      partial: { variant: 'default' as const, label: uiText("Partial") },
      complete: { variant: 'default' as const, label: uiText("Complete") }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return (
      <Badge variant={config.variant} className={status === 'complete' ? 'bg-green-100 text-green-800' : ''}>
        {config.label}
      </Badge>
    );
  };

  const getSubscriptionTypeDisplay = (type: string) => {
    const typeMap = {
      '1year': '1 Year',
      '6months': '6 Months',
      '3months': '3 Months',
      'monthly': 'Monthly'
    };
    return typeMap[type as keyof typeof typeMap] || type;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return uiText("N/A");
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredPayments = studentPayments.filter(payment => {
    const matchesSearch = payment.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || payment.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  const uniqueCategories = [...new Set(studentPayments.map(p => p.category))];

  const getTotalStats = () => {
    const totalRevenue = studentPayments.reduce((sum, payment) => sum + payment.amountPaid, 0);
    const pendingAmount = studentPayments.reduce((sum, payment) => {
      if (payment.status !== 'complete') {
        return sum + (payment.totalAmount - payment.amountPaid);
      }
      return sum;
    }, 0);

    return {
      totalRevenue: totalRevenue / 100, // Convert from cents
      pendingAmount: pendingAmount / 100,
      totalPayments: studentPayments.length,
      completedPayments: studentPayments.filter(p => p.status === 'complete').length
    };
  };

  const stats = getTotalStats();

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;

    setIsDeleting(true);
    try {
      // First delete any related invoices
      const { error: invoiceError } = await supabase
        .from('invoices')
        .delete()
        .eq('payment_instruction_id', paymentToDelete.id);

      if (invoiceError) {
        console.warn('Error deleting related invoices:', invoiceError);
        // Continue with payment deletion even if invoice deletion fails
      }

      // Then delete the payment instruction
      const { error } = await supabase
        .from('payment_instructions')
        .delete()
        .eq('id', paymentToDelete.id);

      if (error) throw error;

      toast({
        title: uiText("Success"),
        description: uiText("Payment deleted successfully"),
      });

      setDeleteDialogOpen(false);
      setPaymentToDelete(null);

      // Call onRefresh if it exists
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      toast({
        title: uiText("Error"),
        description: error.message || "Failed to delete payment. There may be related records that need to be removed first.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

    const formatCurrency = (amount: number, currency: string = defaultCurrency) => {
        const currencySymbol = getCurrencySymbol(currency);
        return `${currencySymbol}${amount.toFixed(2)}`;
    };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">{uiText("Total Revenue")}</p>
                <p className="text-2xl font-bold text-gray-900">{currencySymbol}{stats.totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-yellow-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">{uiText("Pending Amount")}</p>
                <p className="text-2xl font-bold text-gray-900">{currencySymbol}{stats.pendingAmount.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <User className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">{uiText("Total Payments")}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalPayments}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Filter className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">{uiText("Completed")}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedPayments}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-3">
            <span>{uiText("Payment History")}</span>
            <div className="flex flex-wrap items-center gap-2">
            <TaxReportGenerator />
            <Button
              onClick={() => {
                setLoading(true);
                if (onRefresh) {
                  onRefresh();
                }
                // Force a small delay to show loading state
                setTimeout(() => setLoading(false), 500);
              }}
              variant="outline"
              size="sm"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? uiText("Refreshing...") : uiText("Refresh")}
            </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <Label htmlFor="search">{uiText("Search")}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder={uiText("Search students, emails, or categories...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="status-filter">{uiText("Status")}</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{uiText("All Statuses")}</SelectItem>
                  <SelectItem value="pending">{uiText("Pending")}</SelectItem>
                  <SelectItem value="partial">{uiText("Partial")}</SelectItem>
                  <SelectItem value="complete">{uiText("Complete")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category-filter">{uiText("Category")}</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger id="category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{uiText("All Categories")}</SelectItem>
                  {uniqueCategories.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Payment History Table */}
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {filteredPayments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{uiText("No payment records found.")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPayments.map((payment) => (
                    <Card key={payment.id} className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
                          <div>
                            <p className="text-sm font-medium text-gray-500">{uiText("Student")}</p>
                            <p className="font-semibold">{payment.studentName}</p>
                            <p className="text-sm text-gray-500">{payment.email}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-gray-500">{uiText("Category")}</p>
                            <p className="font-semibold">{payment.category}</p>
                            <p className="text-sm text-gray-500">
                              {getSubscriptionTypeDisplay(payment.subscriptionType)}
                            </p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-gray-500">{uiText("Payment Plan")}</p>
                            <p className="font-semibold">{payment.paymentPlan} {' '}{uiText("installment(s)")}</p>
                            <p className="text-sm text-gray-500">
                              {payment.installmentsPaid}/{payment.totalInstallments} {' '}{uiText("paid")}</p>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-gray-500">{uiText("Amount")}</p>
                            <p className="font-semibold">
                              {currencySymbol}{(payment.amountPaid / 100).toFixed(2)} / {currencySymbol}{(payment.totalAmount / 100).toFixed(2)}
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${(payment.amountPaid / payment.totalAmount) * 100}%` }}
                              />
                            </div>
                          </div>

                          <div>
                            <p className="text-sm font-medium text-gray-500">{uiText("Dates")}</p>
                            <p className="text-sm">{uiText("Paid on:")}{' '}{formatDate(payment.paymentDate)}</p>
                            <p className="text-sm">{uiText("Start:")}{' '}{formatDate(payment.startDate)}</p>
                            <p className="text-sm">{uiText("End:")}{' '}{formatDate(payment.endDate)}</p>
                            {payment.nextDueDate && (
                              <p className="text-sm text-orange-600">{uiText("Next Due:")}{' '}{formatDate(payment.nextDueDate)}</p>
                            )}
                          </div>

                          <div className="border-t pt-4 sm:col-span-2 xl:col-span-5">
                            <p className="text-sm font-medium text-gray-500">{uiText("Actions")}</p>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setEditDialogOpen(true);
                                }}
                                className="h-8 px-3 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                {uiText("Edit")}</Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setPaymentToDelete(payment);
                                  setDeleteDialogOpen(true);
                                }}
                                className="h-8 px-3 hover:bg-red-50 hover:border-red-300 text-red-600 hover:text-red-700 transition-colors"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                {uiText("Delete")}</Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {payment.id && (
                                <InvoiceGenerator
                                  paymentId={payment.id}
                                  studentName={payment.studentName}
                                  amount={payment.totalAmount}
                                  category={payment.category}
                                  paymentStatus={payment.status}
                                  onInvoiceGenerated={() => {
                                    if (onRefresh) {
                                      onRefresh();
                                    }
                                  }}
                                />
                              )}
                              {payment.studentId && typeof studentStatuses[payment.studentId] === 'boolean' ? (
                                <StudentStatusToggle
                                  studentId={payment.studentId}
                                  currentStatus={studentStatuses[payment.studentId]}
                                  onStatusChange={(isActive) => {
                                    setStudentStatuses(current => ({ ...current, [payment.studentId]: isActive }));
                                    onRefresh?.();
                                  }}
                                />
                              ) : (
                                <Button variant="outline" size="sm" disabled>{uiText("Student status unavailable")}</Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedPayment && <EditPaymentDialog key={selectedPayment.id} open={editDialogOpen} payment={selectedPayment} onOpenChange={open => { setEditDialogOpen(open); if (!open) setSelectedPayment(null); }} onSaved={onRefresh} />}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiText("Delete Payment")}</AlertDialogTitle>
            <AlertDialogDescription>
              {uiText("Are you sure you want to delete this payment record? This action cannot be undone.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{uiText("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePayment}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {uiText("Deleting...")}</>
              ) : (
                uiText("Delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StudentPaymentsTab;
